import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import {
  Box,
  Button,
  ClickAwayListener,
  Grid,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import React from "react";
import ReactDOM from "react-dom";
import { useLocalization } from "../../../hooks/useLocalization";
import { useCalendarLogic } from "../hooks/useCalendarLogic";
import { POPOVER_W, usePopoverPosition } from "../hooks/usePopoverPosition";
import { CalendarProps } from "../types";
import { CalendarDay } from "./CalendarDay";

export const CalendarPopover: React.FC<CalendarProps> = ({
  open,
  anchorEl,
  onClose,
  value,
  onApply,
  calendarRef,
  minDate,
  maxDate,
  enableFuture = false,
  alwaysVisible = false,
  disabled = false,
  noDefaultValue = false,
  onTempChange,
  hideButtons = false,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const {
    visibleMonth,
    temp,
    setTempValue,
    gridDays,
    handlePrevMonth,
    handleNextMonth,
    isSameDay,
  } = useCalendarLogic({ value, noDefaultValue, onTempChange });

  const { top, left, bottom, maxHeight } = usePopoverPosition(
    anchorEl,
    open,
    alwaysVisible,
  );

  if (!open && !alwaysVisible) return null;

  const content = (
    <ClickAwayListener onClickAway={onClose}>
      <Box
        onMouseDown={(e) => e.stopPropagation()}
        ref={calendarRef}
        sx={{
          ...(alwaysVisible
            ? {
                position: "relative",
                width: "100%",
                maxWidth: POPOVER_W,
              }
            : {
                position: "fixed",
                top,
                bottom,
                left,
                zIndex: 9999,
                width: POPOVER_W,
                border: `1px solid ${theme.colors.secondary[1]}`,
              }),
          maxHeight,
          overflowY: "auto",
          overflowX: "hidden",
          overscrollBehavior: "contain",
          bgcolor: theme.palette.background.paper,
          borderRadius: 3,
          boxShadow: alwaysVisible ? "none" : "0px 4px 20px rgba(0,0,0,.12)",
        }}
      >
        <Stack spacing={0}>
          {/* Header */}
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            pt={2}
            px={4}
          >
            <IconButton
              disabled={disabled}
              onClick={handlePrevMonth}
              size="small"
              sx={{
                color: theme.colors.primary[5],
                "&.Mui-disabled": { color: theme.colors.primary[1] },
              }}
            >
              <ChevronLeft fontSize="small" />
            </IconButton>
            <Typography
              color={disabled ? theme.colors.text[0] : theme.colors.text[6]}
              fontSize={14}
              fontWeight={500}
            >
              {visibleMonth.toLocaleString(undefined, {
                month: "long",
                year: "numeric",
              })}
            </Typography>
            <IconButton
              disabled={disabled}
              onClick={handleNextMonth}
              size="small"
              sx={{
                color: theme.colors.primary[5],
                "&.Mui-disabled": { color: theme.colors.primary[1] },
              }}
            >
              <ChevronRight fontSize="small" />
            </IconButton>
          </Box>

          {/* Weekday Headers */}
          <Grid container pt={2} px={3}>
            {[
              t("company.calendar.weekdays.monday"),
              t("company.calendar.weekdays.tuesday"),
              t("company.calendar.weekdays.wednesday"),
              t("company.calendar.weekdays.thursday"),
              t("company.calendar.weekdays.friday"),
              t("company.calendar.weekdays.saturday"),
              t("company.calendar.weekdays.sunday"),
            ].map((w) => (
              <Grid item key={w} xs={12 / 7}>
                <Box
                  py={1}
                  sx={{
                    color: theme.colors.text[0],
                    fontWeight: 700,
                    fontSize: 12,
                  }}
                  textAlign="center"
                >
                  {w}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Days Grid */}
          <Grid container pb={4} px={3}>
            {gridDays.map(({ date, inMonth }) => (
              <CalendarDay
                date={date}
                disabled={disabled}
                enableFuture={enableFuture}
                inMonth={inMonth}
                isSameDay={isSameDay}
                key={date.getTime()}
                maxDate={maxDate}
                minDate={minDate}
                onApply={onApply}
                onClose={onClose}
                onSelect={(d) => setTempValue(new Date(d))}
                temp={temp}
              />
            ))}
          </Grid>

          {/* Footer Buttons */}
          {!hideButtons && (
            <Box display="flex" gap={1} pb={2} px={3}>
              <Button
                onClick={onClose}
                sx={{
                  height: 40,
                  width: "100%",
                  color: theme.colors.primary[5],
                }}
                variant="secondary"
              >
                {t("company.calendar.cancel")}
              </Button>
              <Button
                onClick={() => {
                  onApply(temp ?? null);
                  onClose();
                }}
                sx={{ height: 40, width: "100%" }}
                variant="contained"
              >
                {t("company.calendar.apply")}
              </Button>
            </Box>
          )}
        </Stack>
      </Box>
    </ClickAwayListener>
  );

  if (alwaysVisible) {
    return content;
  }

  return ReactDOM.createPortal(content, document.body);
};
